import * as React from "react"
import * as Primitives from "@radix-ui/react-alert-dialog"
import { clx, Button } from "~/design-system"

type PromptVariant = "danger" | "confirmation"

const PromptContext = React.createContext<{ variant: PromptVariant }>({
  variant: "danger",
})

const usePromptContext = () => {
  const context = React.useContext(PromptContext)
  if (!context) {
    throw new Error("usePromptContext must be used within a PromptProvider")
  }
  return context
}

type PromptProviderProps = React.PropsWithChildren<{
  variant: PromptVariant
}>

const PromptProvider = ({ variant, children }: PromptProviderProps) => {
  return (
    <PromptContext.Provider value={{ variant }}>
      {children}
    </PromptContext.Provider>
  )
}

/**
 * This component is based on the [Radix UI Alert Dialog](https://www.radix-ui.com/primitives/docs/components/alert-dialog) primitives.
 */
const Root = ({
  /**
   * The variant of the prompt.
   */
  variant = "danger",
  ...props
}: React.ComponentPropsWithoutRef<typeof Primitives.Root> & {
  variant?: PromptVariant
}) => {
  return (
    <PromptProvider variant={variant}>
      <Primitives.Root {...props} />
    </PromptProvider>
  )
}
Root.displayName = "Prompt"

const Trigger = Primitives.Trigger
Trigger.displayName = "Prompt.Trigger"

const Portal = ({ className, ...props }: Primitives.AlertDialogPortalProps) => {
  return <Primitives.AlertDialogPortal className={clx(className)} {...props} />
}
Portal.displayName = "Prompt.Portal"

const Overlay = React.forwardRef<
  React.ElementRef<typeof Primitives.Overlay>,
  React.ComponentPropsWithoutRef<typeof Primitives.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <Primitives.Overlay
      ref={ref}
      className={clx(
        "bg-transparent/30 fixed top-0 bottom-0 left-0 right-0 z-50 overflow-y-auto",
        className
      )}
      {...props}
    />
  )
})
Overlay.displayName = "Prompt.Overlay"

const Title = React.forwardRef<
  React.ElementRef<typeof Primitives.Title>,
  Omit<React.ComponentPropsWithoutRef<typeof Primitives.Title>, "asChild">
>(({ className, children, ...props }, ref) => {
  return (
    <Primitives.Title ref={ref} className={clx(className)} {...props} asChild>
      <h2 className="txt-compact-medium-plus font-semibold leading-normal text-ui-fg-base">
        {children}
      </h2>
    </Primitives.Title>
  )
})
Title.displayName = "Prompt.Title"

const Content = React.forwardRef<
  React.ElementRef<typeof Primitives.Content>,
  React.ComponentPropsWithoutRef<typeof Primitives.Content>
>(({ className, ...props }, ref) => {
  return (
    <Portal>
      <Overlay />
      <Primitives.Content
        ref={ref}
        className={clx(
          "bg-grey-0 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-dialogue-content",
          "flex flex-col w-full max-w-[400px] z-50 rounded-lg border focus-visible:outline-none",
          className
        )}
        {...props}
      />
    </Portal>
  )
})
Content.displayName = "Prompt.Content"

const Description = React.forwardRef<
  React.ElementRef<typeof Primitives.Description>,
  React.ComponentPropsWithoutRef<typeof Primitives.Description>
>(({ className, ...props }, ref) => {
  return (
    <Primitives.Description
      ref={ref}
      className={clx("text-ui-fg-subtle txt-base-regular", className)}
      {...props}
    />
  )
})
Description.displayName = "Prompt.Description"

const Action = React.forwardRef<
  React.ElementRef<typeof Primitives.Action>,
  Omit<React.ComponentPropsWithoutRef<typeof Primitives.Action>, "asChild"> & {
    isLoading?: boolean
  }
>(({ className, children, type, isLoading, disabled, ...props }, ref) => {
  const { variant } = usePromptContext()

  return (
    <Primitives.Action ref={ref} className={className} {...props} asChild>
      <Button
        size="small"
        type={type}
        variant={variant === "danger" ? "danger" : "primary"}
        isLoading={isLoading}
        disabled={disabled}
      >
        {children}
      </Button>
    </Primitives.Action>
  )
})
Action.displayName = "Prompt.Action"

const Cancel = React.forwardRef<
  React.ElementRef<typeof Primitives.Cancel>,
  Omit<React.ComponentPropsWithoutRef<typeof Primitives.Cancel>, "asChild"> & {
    disabled?: boolean
  }
>(({ className, children, disabled, ...props }, ref) => {
  return (
    <Primitives.Cancel ref={ref} className={clx(className)} {...props} asChild>
      <Button size="small" variant="secondary" disabled={disabled}>
        {children}
      </Button>
    </Primitives.Cancel>
  )
})
Cancel.displayName = "Prompt.Cancel"

/**
 * This component is based on the `div` element and supports all of its props
 */
const Header = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={clx("flex flex-col gap-y-1 px-6 pt-6", className)}
      {...props}
    />
  )
}
Header.displayName = "Prompt.Header"

/**
 * This component is based on the `div` element and supports all of its props
 */
const Footer = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={clx("flex items-center justify-end gap-x-2 p-6", className)}
      {...props}
    />
  )
}
Footer.displayName = "Prompt.Footer"

const Prompt = Object.assign(Root, {
  Trigger,
  Content,
  Title,
  Description,
  Action,
  Cancel,
  Header,
  Footer,
})

export { Prompt }
